import { Autocomplete, TextField } from "@mui/material";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { BsFileImage, BsFilm, BsStarFill } from "react-icons/bs";
import Link from "next/link";
import { generateIcon, getFormattedFileSize, formatCostInUSD, useLocalStorage } from "utils";
import { useMediaContext } from "../../context/MediaContext";
import { MediaEnum } from "./enums";
import type { PhotoItemType, VideoItemType } from "./types";
import { InfoTooltipButton } from "../InfoTooltipButton";

interface IMediaInfoProps {
  items: (VideoItemType | PhotoItemType)[];
  showFilter: boolean;
  cost?: number | null; // AI cost in cents
  budgetPercent?: number | "unlimited" | null; // AI budget remaining % – visible to everyone
  magicServices?: string | null;
}

export default function MediaInfo({ items, showFilter, cost, budgetPercent, magicServices }: IMediaInfoProps) {
  const [pageSize, setPageSize] = useLocalStorage<number>(
    "galleryPageSize",
    100
  );
  const { setPageSizeState } = useMediaContext();
  const t = useTranslations();
  const [mediaInfo, setMediaInfo] = useState({
    photoCount: 0,
    videoCount: 0,
    fileSize: 0,
  });

  useEffect(() => {
    setMediaInfo(
      items.reduce(
        (acc, item) => {
          if (item.mediaType === MediaEnum.Video) {
            acc.videoCount++;
          } else {
            acc.photoCount++;
          }

          acc.fileSize += item.fileLength;

          return acc;
        },
        { photoCount: 0, videoCount: 0, fileSize: 0 }
      )
    );

    setPageSizeState(pageSize);
    // eslint-disable-next-line
  }, [items]);

  return (
    <div className="text-[14px] bg-base-200/25 px-1 rounded-lg text-gray-500 flex flex-row justify-between">
      <div className="flex items-center">
        <p className="mb-0 mx-2 flex items-center">
          {generateIcon(
            BsFileImage,
            `transition text-[16px] ${
              mediaInfo.photoCount > 0
                ? "text-purple-700/75 group-hover:text-purple-700"
                : "text-gray-400"
            }`
          )}
          <span className="ml-1">{mediaInfo.photoCount}</span>
        </p>

        <p className="mb-0 mx-2 flex items-center">
          {generateIcon(
            BsFilm,
            `transition text-[16px] ${
              mediaInfo.videoCount > 0
                ? "text-purple-700/75 group-hover:text-purple-700"
                : "text-gray-400"
            }`
          )}
          <span className="ml-1">{mediaInfo.videoCount}</span>
        </p>

        {mediaInfo.fileSize ? (
          <p className="font-medium text-center">
            ({getFormattedFileSize(mediaInfo.fileSize)})
          </p>
        ) : null}

        {cost !== undefined && cost !== null ? (
          <p className="mb-0 mx-2 flex items-center font-medium">
            <span className="text-purple-700/75">{t("Reservation.AICost")}:</span>
            <span className="ml-1">{formatCostInUSD(cost)}</span>
          </p>
        ) : null}

        {budgetPercent !== null && budgetPercent !== undefined ? (
          <p className="mb-0 mx-2 flex items-center font-medium">
            <span className="text-purple-700/75">{t("Reservation.AIBudgetPercent")}:</span>
            {budgetPercent === "unlimited" ? (
              <span className="ml-1">∞</span>
            ) : (
              <span className={`ml-1 ${budgetPercent <= 10 ? "text-red-500" : budgetPercent <= 30 ? "text-orange-500" : "text-green-600"}`}>
                {budgetPercent}%
              </span>
            )}
            <span className="ml-1.5 flex items-center">
              <InfoTooltipButton text={t("Global.aiBudgetInfoTooltip")} />
            </span>
          </p>
        ) : null}

        {magicServices !== undefined && (
          <Link
            className="flex flex-row gap-2 items-center w-fit border-[#1e293b] border-2 rounded-full px-4 py-1 text-[#1e293b] font-semibold hover:bg-[#1e293b] hover:text-white text-[14px]"
            href="/manageMagicAI"
          >
            <BsStarFill />
            {!magicServices
              ? t("Global.activateMagicAI")
              : t("Global.topUpAI")}
          </Link>
        )}
      </div>
      {showFilter ? (
        <Autocomplete
          disablePortal
          id="pageSize"
          defaultValue={`${pageSize}`}
          options={["20", "50", "100", "200", "500", "1000"]}
          sx={{ width: 150 }}
          renderInput={(params) => (
            <TextField {...params} label={t("Gallery.pageSize")} />
          )}
          disableClearable
          onChange={(event: any, newValue: string) => {
            setPageSizeState(Number(newValue));
            setPageSize(Number(newValue));
          }}
        />
      ) : null}
    </div>
  );
}
